import { type ComponentProps, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ANY_SPORT_MARKET_SHOP_AUCTIONS } from '@sorare/core/src/constants/__generated__/routes';
import type { AlgoliaCardIndexes } from '@sorare/core/src/contexts/config';
import { useCardsOnSale } from '@sorare/core/src/hooks/search/useCardsOnSale';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useSo5Competitions } from '@sorare/core/src/hooks/useSo5Competitions';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { ClickMarketHomeSection_Target } from '@sorare/core/src/protos/events/so5/shared/events';

import { CardsShow } from '../CardsShow';
import { HeaderSection } from '../HeaderSection';

type Props = {
  sport: Sport;
  track?: ComponentProps<typeof CardsShow>['track'];
};

export const TrendingAuctions = ({ sport, track }: Props) => {
  const { lifecycle } = useLifecycle();
  const rarity =
    lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport]?.rarity || Rarity.limited;
  const competitionSlug =
    lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport]?.competitionSlug || '';

  const { scoutableSo5Competitions } = useSo5Competitions();
  const { algoliaFilters } =
    scoutableSo5Competitions.find(
      competition => competition.slug === competitionSlug
    ) || {};

  const props = useMemo(
    () => ({
      sport,
      primaryMarket: true,
      primaryOffer: false,
      rarities: [rarity],
      distinct: true,
      index: 'Cards On Sale Bids Count' as keyof AlgoliaCardIndexes,
      addtionalAlgoliaFilters: algoliaFilters,
      limit: 20,
    }),
    [rarity, algoliaFilters, sport]
  );

  const { slugs, loading } = useCardsOnSale(props);

  return (
    <Vertical gap={3}>
      <HeaderSection
        title={
          <FormattedMessage
            id="TrendingAuctions.title"
            defaultMessage="Trending Auctions"
          />
        }
        button={{
          label: (
            <FormattedMessage
              id="TrendingAuctions.viewAll"
              defaultMessage="All Auctions"
            />
          ),
          to: generateSportPath(ANY_SPORT_MARKET_SHOP_AUCTIONS, { sport }),
          onClick: () => {
            track?.({
              target: ClickMarketHomeSection_Target.ALL_BUTTON,
            });
          },
        }}
      />
      <CardsShow
        hits={slugs.map(slug => ({ slug }))}
        loading={loading}
        sport={sport}
        track={track}
      />
    </Vertical>
  );
};
